import { type ReactNode, useEffect, useState } from 'react';
import { useCustom, useCustomMutation, useGo } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { Loader2, Workflow } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ColorPicker } from '@/components/ui/color-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { FormHeader } from '@/components/forms';
import type { LeadFunnel, LeadStatusAction, LeadStatusConfig } from '../types';

interface FunnelPayload {
  name: string;
  description?: string;
  position: number;
  isDefault: boolean;
  isActive: boolean;
}

interface StagePayload {
  leadFunnelId: number;
  name: string;
  description?: string;
  color: string;
  position?: number;
  isInitial?: boolean;
  isFinal?: boolean;
  onEnterActions: LeadStatusAction[];
  onExitActions: LeadStatusAction[];
}

export function LeadFunnelsForm() {
  const go = useGo();
  const { id } = useParams();
  const isEdit = id !== undefined;

  const funnelQuery = useCustom<LeadFunnel>({
    url: `/leads/funnels/${id ?? ''}`,
    method: 'get',
    queryOptions: { enabled: isEdit },
  });
  const funnel = funnelQuery.query?.data?.data;

  const handleBack = () => go({ to: '/leads/funnels' });

  if (isEdit && (funnelQuery.query?.isLoading ?? false)) {
    return <LoadingState />;
  }

  return (
    <EntityFormShell
      title={isEdit ? 'Редактировать воронку' : 'Создать воронку'}
      description="Воронка объединяет этапы и показывает лиды по этим этапам"
      onBack={handleBack}
    >
      <LeadFunnelFields funnel={funnel} position={funnel?.position ?? 0} onCancel={handleBack} onSuccess={handleBack} />
    </EntityFormShell>
  );
}

export function LeadFunnelFields({
  funnel,
  position,
  onCancel,
  onSuccess,
}: {
  funnel?: LeadFunnel;
  position: number;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const isEdit = funnel !== undefined;
  const [name, setName] = useState(funnel?.name ?? '');
  const [description, setDescription] = useState(funnel?.description ?? '');
  const [isDefault, setIsDefault] = useState(funnel?.isDefault ?? false);
  const [isActive, setIsActive] = useState(funnel?.isActive ?? true);
  const createMutation = useCustomMutation();
  const updateMutation = useCustomMutation();
  const isSaving = createMutation.mutation.isPending || updateMutation.mutation.isPending;

  useEffect(() => {
    setName(funnel?.name ?? '');
    setDescription(funnel?.description ?? '');
    setIsDefault(funnel?.isDefault ?? false);
    setIsActive(funnel?.isActive ?? true);
  }, [funnel]);

  const handleSubmit = () => {
    if (!name.trim()) {
      return;
    }
    const values: FunnelPayload = {
      name: name.trim(),
      description: description.trim() || undefined,
      position,
      isDefault,
      isActive,
    };
    const options = { onSuccess };
    if (isEdit) {
      updateMutation.mutate({ url: `/leads/funnels/${funnel.id}`, method: 'patch', values }, options);
      return;
    }
    createMutation.mutate({ url: '/leads/funnels', method: 'post', values }, options);
  };

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="lead-funnel-name">Название</Label>
        <Input id="lead-funnel-name" value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="lead-funnel-description">Описание</Label>
        <Textarea id="lead-funnel-description" value={description} onChange={(event) => setDescription(event.target.value)} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <SwitchRow id="lead-funnel-default" label="По умолчанию" checked={isDefault} onChange={setIsDefault} />
        <SwitchRow id="lead-funnel-active" label="Активна" checked={isActive} onChange={setIsActive} />
      </div>
      <FormActions isSaving={isSaving} canSave={name.trim().length > 0} onSave={handleSubmit} onCancel={onCancel} />
    </>
  );
}

export function LeadFunnelStageForm() {
  const go = useGo();
  const { id, funnelId } = useParams();
  const isEdit = id !== undefined;

  const statusQuery = useCustom<LeadStatusConfig>({
    url: `/leads/statuses/${id ?? ''}`,
    method: 'get',
    queryOptions: { enabled: isEdit },
  });
  const status = statusQuery.query?.data?.data;

  const handleBack = () => go({ to: '/leads/funnels' });

  if (isEdit && (statusQuery.query?.isLoading ?? false)) {
    return <LoadingState />;
  }

  return (
    <EntityFormShell
      title={isEdit ? 'Редактировать этап' : 'Создать этап'}
      description="Этап принадлежит конкретной воронке и становится колонкой на доске лидов"
      onBack={handleBack}
    >
      <LeadFunnelStageFields
        funnelId={Number(funnelId ?? status?.leadFunnelId)}
        status={status}
        position={status?.position ?? 0}
        onCancel={handleBack}
        onSuccess={handleBack}
      />
    </EntityFormShell>
  );
}

export function LeadFunnelStageFields({
  funnelId,
  status,
  position,
  onCancel,
  onSuccess,
}: {
  funnelId: number;
  status?: LeadStatusConfig;
  position: number;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const isEdit = status !== undefined;
  const [name, setName] = useState(status?.name ?? '');
  const [description, setDescription] = useState(status?.description ?? '');
  const [color, setColor] = useState(status?.color ?? '#6b7280');
  const [positionValue, setPositionValue] = useState(String((status?.position ?? position) + 1));
  const [isInitial, setIsInitial] = useState(status?.isInitial ?? false);
  const [isFinal, setIsFinal] = useState(status?.isFinal ?? false);
  const createMutation = useCustomMutation();
  const updateMutation = useCustomMutation();
  const isSaving = createMutation.mutation.isPending || updateMutation.mutation.isPending;
  const isSystem = status?.isSystem ?? false;

  useEffect(() => {
    setName(status?.name ?? '');
    setDescription(status?.description ?? '');
    setColor(status?.color ?? '#6b7280');
    setPositionValue(String((status?.position ?? position) + 1));
    setIsInitial(status?.isInitial ?? false);
    setIsFinal(status?.isFinal ?? false);
  }, [position, status]);

  const handleSubmit = () => {
    const parsedPosition = Number(positionValue);
    const normalizedPosition = Number.isInteger(parsedPosition) && parsedPosition > 0
      ? parsedPosition - 1
      : Number.NaN;
    if (!name.trim() || !Number.isFinite(funnelId) || funnelId <= 0 || (!isSystem && !Number.isFinite(normalizedPosition))) {
      return;
    }
    const basePayload = {
      leadFunnelId: funnelId,
      name: name.trim(),
      description: description.trim() || undefined,
      color,
      onEnterActions: status?.onEnterActions ?? [],
      onExitActions: status?.onExitActions ?? [],
    };
    const values: StagePayload = isSystem
      ? basePayload
      : { ...basePayload, position: normalizedPosition, isInitial, isFinal };
    const options = { onSuccess };
    if (isEdit) {
      updateMutation.mutate({ url: `/leads/statuses/${status.id}`, method: 'patch', values }, options);
      return;
    }
    createMutation.mutate({ url: '/leads/statuses', method: 'post', values }, options);
  };

  return (
    <>
      {isSystem && <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700">Системный этап: можно менять только название, описание и цвет.</div>}
      <div className="space-y-2">
        <Label htmlFor="lead-stage-name">Название</Label>
        <Input id="lead-stage-name" value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="lead-stage-description">Описание</Label>
        <Textarea id="lead-stage-description" value={description} onChange={(event) => setDescription(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="lead-stage-color">Цвет</Label>
        <ColorPicker id="lead-stage-color" value={color} onChange={setColor} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="lead-stage-position">Порядок</Label>
        <Input
          id="lead-stage-position"
          type="number"
          min={1}
          step={1}
          value={positionValue}
          disabled={isSystem}
          onChange={(event) => setPositionValue(event.target.value)}
        />
        <p className="text-xs text-muted-foreground">1 — первый этап в воронке.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <SwitchRow id="lead-stage-initial" label="Начальный этап" checked={isInitial} onChange={setIsInitial} disabled={isSystem} />
        <SwitchRow id="lead-stage-final" label="Финальный этап" checked={isFinal} onChange={setIsFinal} disabled={isSystem} />
      </div>
      <FormActions
        isSaving={isSaving}
        canSave={name.trim().length > 0 && (isSystem || (Number.isInteger(Number(positionValue)) && Number(positionValue) > 0))}
        onSave={handleSubmit}
        onCancel={onCancel}
      />
    </>
  );
}

function EntityFormShell({ title, description, onBack, children }: {
  title: string;
  description: string;
  onBack: () => void;
  children: ReactNode;
}) {
  return (
    <div className="space-y-4">
      <FormHeader title={title} description={description} icon={<Workflow className="h-5 w-5" />} onBack={onBack} sticky />
      <Card className="max-w-2xl">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm">Основное</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-2 space-y-4">{children}</CardContent>
      </Card>
    </div>
  );
}

function SwitchRow({ id, label, checked, onChange, disabled = false }: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between rounded-md border p-3">
      <Label htmlFor={id}>{label}</Label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

function FormActions({ isSaving, canSave, onSave, onCancel }: {
  isSaving: boolean;
  canSave: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex gap-2">
      <Button onClick={onSave} disabled={!canSave || isSaving}>
        {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
        Сохранить
      </Button>
      <Button variant="outline" onClick={onCancel}>Отмена</Button>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex justify-center py-16">
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>
  );
}
